<template>
  <div>
    <div v-if="measurementsLoading">
      {{ $t("bodyprint_queue.measurements_table.loading") }}
    </div>
    <div v-else>
      <div v-if="itemsForQueue.length === 0">
        {{ $t("bodyprint_queue.measurements_table.no_data") }}
      </div>
      <div v-else>
        <b>{{ $t("bodyprint_queue.measurements_table.title") }}</b>

        <table
          class="table-fixed w-full border-solid px-4 mt-3 data-table-right-section overflow-y-auto table-parent"
        >
          <thead>
            <tr>
              <th class="w-3/12 px-2 pb-2 text-left font-semibold">
                {{
                  $t("bodyprint_queue.measurements_table.headers.retailer_code")
                }}
              </th>
              <th class="w-3/12 px-2 pb-2 text-left font-semibold">
                {{ $t("bodyprint_queue.measurements_table.headers.retailer") }}
              </th>
              <th class="w-3/12 px-2 pb-2 text-left font-semibold">
                {{
                  $t("bodyprint_queue.measurements_table.headers.last_editor")
                }}
              </th>
              <th class="w-3/12 px-2 pb-2 text-left font-semibold">
                {{
                  $t(
                    "bodyprint_queue.measurements_table.headers.last_updated_at"
                  )
                }}
              </th>
              <th class="w-3/12 px-2 pb-2 text-left font-semibold">
                {{
                  $t(
                    "bodyprint_queue.measurements_table.headers.measurement_status"
                  )
                }}
              </th>
              <th class="w-3/12 px-2 pb-2 text-left font-semibold">
                {{
                  $t(
                    "bodyprint_queue.bodyprint_table.headers.user_permission_status"
                  )
                }}
              </th>
              <th class="w-3/12 px-2 pb-2 text-left font-semibold">
                {{
                  $t(
                    "bodyprint_queue.measurements_table.headers.passport_status"
                  )
                }}
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(queueItem, index) in itemsForQueue"
              :key="index"
              v-bind:class="[
                'cursor-pointer hover:bg-gray-50',
                index === itemsForQueue.length - 1 ? '' : 'border-b'
              ]"
            >
              <td class="py-3 px-2 text-left text-sm">
                {{ queueItem.retailer_code }}
              </td>
              <td class="py-3 px-2 text-left text-sm">
                {{ queueItem.retailer_name }}
              </td>
              <td class="py-3 px-2 text-left text-sm">
                {{ queueItem.last_editor !== "" ? queueItem.last_editor : "-" }}
              </td>
              <td class="py-3 px-2 text-left text-sm">
                {{ queueItem.last_updated_at }}
              </td>
              <td class="py-3 px-2 text-left text-sm">
                {{
                  $t(
                    `general.status.bodyprint.${queueItem.status.toLowerCase()}`
                  )
                }}
              </td>
              <td class="py-3 px-2 text-left text-sm">
                {{
                  $t(
                    `general.status.user_permission.${queueItem.user.permission.toLowerCase()}`
                  )
                }}
              </td>
              <td class="py-3 px-2 text-left text-sm">
                {{
                  $t(
                    `general.status.bodyprint.${queueItem.bodyprint.archived.toLowerCase()}`
                  )
                }}
              </td>
              <td class="py-3 px-2 text-left">
                <dots-dropdown>
                  <div
                    v-if="canView(queueItem)"
                    class="dropdown-item"
                    @click="handleView(queueItem)"
                  >
                    {{ $t("bodyprint_queue.measurements_table.buttons.view") }}
                  </div>
                  <div
                    v-if="canEdit(queueItem)"
                    class="dropdown-item"
                    @click="handleEdit(queueItem)"
                  >
                    {{ $t("bodyprint_queue.measurements_table.buttons.edit") }}
                  </div>
                  <div
                    v-if="canViewBodyMeasurements(queueItem)"
                    class="dropdown-item"
                    @click="handleSizes(queueItem)"
                  >
                    {{
                      $t(
                        "bodyprint_queue.measurements_table.buttons.body_measurements"
                      )
                    }}
                  </div>
                </dots-dropdown>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import DotsDropdown from "@/components/general/dots-dropdown.vue";
import { BodyprintQueueStatus } from "@/enums/modules/BodyprintQueueStatus";
import { BlurQueueStatus } from "@/enums/modules/BlurQueueStatus";
import { UserPermissionStatus } from "@/enums/modules/UserPermissionStatus";

export default {
  name: "BodyPrintMeasurementsTable",
  components: { DotsDropdown },
  props: {
    itemsForQueue: {
      type: Array,
      default: () => []
    },
    measurementsLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    currentUserEmail() {
      return this.$store.getters["auth/getAuth"].userEmail;
    },
    mostRecentQueueItem() {
      return this.itemsForQueue.reduce((mostRecent, queueItem) => {
        if (!mostRecent) return queueItem;

        return new Date(queueItem.last_updated_at) >
          new Date(mostRecent.last_updated_at)
          ? queueItem
          : mostRecent;
      }, null);
    }
  },
  methods: {
    canView(queueItem) {
      const hasImages = queueItem.image_count > 0;
      const isLastEditor = queueItem.last_editor === this.currentUserEmail;
      let correctBlurStatus =
        queueItem.bodyprint.blur_status === BlurQueueStatus.BLURRED;

      if (
        queueItem.status === BodyprintQueueStatus.IN_REVIEW ||
        queueItem.status === BodyprintQueueStatus.IN_PROGRESS ||
        queueItem.status === BodyprintQueueStatus.COMPLETED_IN_REVIEW ||
        queueItem.status === BodyprintQueueStatus.COMPLETED_IN_PROGRESS
      ) {
        if (isLastEditor) {
          // Last editor can also edit if the blur status is REJECTED,
          // so that the user can fix an incorrect closing of the measurement
          correctBlurStatus =
            queueItem.bodyprint.blur_status === BlurQueueStatus.BLURRED ||
            queueItem.bodyprint.blur_status === BlurQueueStatus.REJECTED;

          return hasImages && correctBlurStatus;
        } else {
          return false;
        }
      }

      return hasImages && correctBlurStatus;
    },
    canEdit(queueItem) {
      const isMostRecent = queueItem === this.mostRecentQueueItem;
      const isArchived =
        queueItem.bodyprint.archived ===
          BodyprintQueueStatus.ARCHIVED_BY_USER ||
        queueItem.bodyprint.archived ===
          BodyprintQueueStatus.ARCHIVED_BY_RETAILER;

      const isUserRejected =
        queueItem.user.permission === UserPermissionStatus.PENDING_WITHOUT_USER;

      return (
        this.canView(queueItem) &&
        isMostRecent &&
        !isArchived &&
        !isUserRejected
      );
    },
    canViewBodyMeasurements(queueItem) {
      return (
        queueItem.user.permission !== UserPermissionStatus.PENDING_WITHOUT_USER
      );
    },
    handleView(queueItem) {
      this.$emit("handle-view", queueItem);
    },
    handleEdit(queueItem) {
      this.$emit("handle-edit", queueItem);
    },
    handleSizes(queueItem) {
      this.$emit("handle-sizes", queueItem);
    }
  }
};
</script>
